/**
 * Result Outbox — bulletproof submission queue for game results.
 *
 * Why this exists
 * ---------------
 * The SUPERTMATIK platform runs school competitions with prizes. A burst of
 * 30 students on a school WiFi network has a non-zero chance that, at the
 * exact second a result is POSTed, the network blips. Without a safety net
 * the result is silently lost — the student walks away thinking they won,
 * but the leaderboard doesn't show their time and the teacher cannot
 * award the prize.
 *
 * How the outbox works
 * --------------------
 * 1. Every `submitResult(endpoint, payload, opts)` call attaches a
 *    `client_submission_id` (UUID v4) to the payload and tries an
 *    immediate POST.
 * 2. On any failure (network error, timeout, 5xx, offline) the payload is
 *    persisted to localStorage in the outbox queue and the function still
 *    resolves so the UI flows normally.
 * 3. A background worker drains the queue:
 *      • Every 5 seconds.
 *      • When `window.online` event fires (browser regains connectivity).
 *      • When the app boots (so a result queued just before a tab refresh
 *        gets sent the next time the user opens the site).
 *    Backoff between retries is exponential (5s, 10s, 20s, 40s, …) capped
 *    at 5 minutes.
 * 4. After 24 hours the entry is marked `expired` so it stops consuming
 *    network attempts, but it stays in localStorage so an admin can
 *    inspect / force-resend via /admin/resultados-pendentes.
 * 5. Backend idempotency (see routes/_idempotency.py) guarantees that
 *    re-sending the same `client_submission_id` does NOT create
 *    duplicate rows — the original response is replayed.
 *
 * Storage shape (localStorage key `supert_result_outbox`):
 *   [
 *     { id, endpoint, payload, status, attempts,
 *       firstAttemptAt, lastAttemptAt, nextAttemptAt, expiresAt,
 *       lastError, response? }
 *   ]
 *
 * The module is intentionally framework-agnostic so it can be reused if we
 * ever migrate this file to a Service Worker for true background sync.
 */

import { api } from "@/lib/api";

const STORAGE_KEY = "supert_result_outbox";
const TICK_MS = 5_000;             // Drain every 5 seconds while the tab is open.
const MAX_BACKOFF_MS = 5 * 60_000; // 5 minutes.
const EXPIRE_AFTER_MS = 24 * 60 * 60_000; // 24 hours.
const REQUEST_TIMEOUT_MS = 10_000; // Per individual POST.

// In-memory copy of the outbox so a tight loop of writes doesn't hammer the
// localStorage parser. Always rehydrated from disk on each call to guard
// against multi-tab interleaving.
let memoryCache = null;
let drainInFlight = false;
let listeners = new Set();

function uuid() {
  // crypto.randomUUID is available in all modern browsers + Safari ≥15.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Tiny fallback (RFC4122 v4 collisions are astronomically unlikely here).
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (Number(c) ^ (Math.random() * 16) >> (Number(c) / 4)).toString(16),
  );
}

function read() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    memoryCache = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(memoryCache)) memoryCache = [];
  } catch {
    memoryCache = [];
  }
  return memoryCache;
}

function write(queue) {
  memoryCache = queue;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    /* quota exceeded / private mode — silently degrade */
  }
  notify();
}

function notify() {
  // Snapshot for subscribers so listeners can do shallow equality without
  // accidentally mutating the underlying array.
  const snap = (memoryCache || []).map((e) => ({ ...e }));
  for (const cb of listeners) {
    try { cb(snap); } catch { /* listener errors must not break the queue */ }
  }
}

function nextBackoffMs(attempts) {
  // 5s, 10s, 20s, 40s, ..., capped at 5min. attempts is 1-based.
  return Math.min(MAX_BACKOFF_MS, 5_000 * Math.pow(2, Math.max(0, attempts - 1)));
}

function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
}

/* ------------------------------------------------------------------ */
/*  PUBLIC API                                                         */
/* ------------------------------------------------------------------ */

/**
 * Submit a game result. Returns a Promise that:
 *   • Resolves with `{ ok: true, response, queued: false }` if the server
 *     accepted the result on the first try.
 *   • Resolves with `{ ok: true, queued: true, id }` if the result is
 *     parked in the outbox (network failure). The caller should still
 *     show the win/lose screen — the result WILL be delivered later.
 *   • Resolves with `{ ok: false, error }` only if the input itself is
 *     invalid (no endpoint / no payload). Network errors never throw.
 *
 * `endpoint` MUST be one of the known `/api/...` paths handled by the
 * server's idempotency layer.
 */
export async function submitResult(endpoint, payload, opts = {}) {
  if (!endpoint || typeof endpoint !== "string") {
    return { ok: false, error: "invalid endpoint" };
  }
  if (!payload || typeof payload !== "object") {
    return { ok: false, error: "invalid payload" };
  }

  const id = payload.client_submission_id || uuid();
  const fullPayload = { ...payload, client_submission_id: id };
  const now = Date.now();

  // First-shot attempt (skipped when we know we're offline — straight to queue).
  if (isOnline()) {
    try {
      const res = await api.post(endpoint, fullPayload, { timeout: REQUEST_TIMEOUT_MS });
      // Success on first try — no need to persist to outbox.
      return { ok: true, queued: false, response: res.data, id };
    } catch (err) {
      // Fall through to queueing logic below.
      opts.firstError = errorSummary(err);
    }
  }

  // Persist and let the worker drain.
  const queue = read();
  const entry = {
    id,
    endpoint,
    payload: fullPayload,
    status: "pending",
    attempts: 1,
    firstAttemptAt: now,
    lastAttemptAt: now,
    nextAttemptAt: now + nextBackoffMs(1),
    expiresAt: now + EXPIRE_AFTER_MS,
    lastError: opts.firstError || (isOnline() ? null : "offline"),
    description: opts.description || endpoint,
  };
  queue.push(entry);
  write(queue);
  // Kick the worker immediately — maybe the failure was transient.
  scheduleDrain(0);
  return { ok: true, queued: true, id };
}

/** Read the current outbox snapshot (read-only). */
export function getOutbox() {
  return read().slice();
}

/** Count of entries waiting to be sent (pending + retrying, NOT expired). */
export function pendingCount() {
  return read().filter((e) => e.status === "pending").length;
}

/** Subscribe to outbox changes. Returns an unsubscribe function. */
export function subscribe(callback) {
  listeners.add(callback);
  // Push initial snapshot to bring the subscriber in sync.
  try { callback(read().slice()); } catch { /* ignored */ }
  return () => listeners.delete(callback);
}

/** Force a drain cycle (used by the admin "Reenviar agora" button). */
export async function forceDrain() {
  return drainNow(true);
}

/** Remove a single entry from the outbox (admin "Eliminar" button). */
export function removeEntry(id) {
  const queue = read().filter((e) => e.id !== id);
  write(queue);
}

/** Remove every entry — used in tests and from the admin "Limpar tudo". */
export function clearOutbox() {
  write([]);
}

/* ------------------------------------------------------------------ */
/*  BACKGROUND WORKER                                                  */
/* ------------------------------------------------------------------ */

let drainTimer = null;

function scheduleDrain(delayMs = TICK_MS) {
  if (drainTimer) clearTimeout(drainTimer);
  drainTimer = setTimeout(() => {
    drainTimer = null;
    drainNow(false);
  }, Math.max(0, delayMs));
}

async function drainNow(force) {
  if (drainInFlight) return;
  drainInFlight = true;
  try {
    const queue = read();
    if (!queue.length) {
      // Nothing to do — re-arm the next idle tick.
      scheduleDrain(TICK_MS);
      return;
    }
    const now = Date.now();
    const ready = queue.filter((e) =>
      e.status === "pending" && (force || e.nextAttemptAt <= now) && isOnline()
    );

    for (const entry of ready) {
      // Expire after the 24h ceiling regardless of retry budget.
      if (now > entry.expiresAt) {
        entry.status = "expired";
        entry.lastError = "expired (24h ceiling reached)";
        continue;
      }
      try {
        const res = await api.post(entry.endpoint, entry.payload, { timeout: REQUEST_TIMEOUT_MS });
        entry.status = "synced";
        entry.response = res.data;
        entry.lastAttemptAt = Date.now();
      } catch (err) {
        entry.attempts = (entry.attempts || 1) + 1;
        entry.lastAttemptAt = Date.now();
        entry.nextAttemptAt = entry.lastAttemptAt + nextBackoffMs(entry.attempts);
        entry.lastError = errorSummary(err);
      }
    }

    // Remove synced entries; keep pending and expired ones in storage.
    const filtered = queue.filter((e) => e.status !== "synced");
    write(filtered);
  } finally {
    drainInFlight = false;
    scheduleDrain(TICK_MS);
  }
}

function errorSummary(err) {
  if (!err) return "unknown error";
  if (err.response) {
    return `HTTP ${err.response.status}`;
  }
  if (err.code === "ECONNABORTED") return "timeout";
  if (err.message === "Network Error") return "network error";
  return String(err.message || err).slice(0, 120);
}

/* ------------------------------------------------------------------ */
/*  BOOT                                                               */
/* ------------------------------------------------------------------ */

/**
 * Boot the outbox worker. Idempotent. Wire it up once from App.js so a
 * result queued just before a tab refresh is delivered on the next page
 * load.
 */
export function bootOutbox() {
  if (typeof window === "undefined") return;
  if (window.__SUPERT_OUTBOX_BOOTED) return;
  window.__SUPERT_OUTBOX_BOOTED = true;

  // Kick a drain right after boot — there might be entries from a previous tab.
  scheduleDrain(500);

  // Drain immediately when the browser regains connectivity.
  window.addEventListener("online", () => scheduleDrain(0));

  // Also try when the tab becomes visible again (focus from background).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") scheduleDrain(0);
  });
}
